import { Eye } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { EmailFrame } from '@/components/ui/EmailFrame'
import { Sheet } from '@/components/ui/Sheet'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { useFormat } from '@/i18n/useFormat'
import { statusFromWire } from '@/features/cases/api/caseWire'
import { useCaseStatusSend, useCaseStatusSends } from '@/features/cases/api/useStatusEmail'
import type { StatusSendEntry } from '@/types/domain'

/* «Отправленные статусы» — what the client was told, and when (S-15).
 *
 * The status send journal: one row per status email that went out on this
 * case, newest first, written inside the send's own transaction with the
 * subject and HTML exactly as the outbox carried them. It replaced the status
 * PDF the send used to file, so "what did we tell the client on Tuesday" has
 * one answer and it is the email itself, not a document rendered beside it.
 *
 * It heads the case's history sheet («⋯» → «История»), above the feed: the
 * feed records THAT a status was sent, this records WHAT it said.
 *
 * What a row prints is the journal's own facts — the sender, the recipient's
 * name, the language, the case's status at the send and whether it went as a
 * consolidated summary. The ADDRESS is printed only when it is served: the
 * server leaves the key out for a reader without `directory.contact.read`, and
 * absence here is not an empty address. The stored email itself carries no
 * address (the renderer is never given one), and it is shown through
 * `EmailFrame`, never as markup. A consolidated summary prints EVERY case it
 * covered, so the server serves its body only to a reader who reaches all of
 * them; for anyone else the entry comes back with `bodyWithheld` and no subject
 * or HTML, and the viewer says so in one line instead.
 *
 * Opening a row reads that one entry; the list never carries the HTML. */

export function StatusSendsList({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const format = useFormat()
  const titleId = useId()
  const [openId, setOpenId] = useState<string | null>(null)

  const sends = useCaseStatusSends(caseId)

  /** Date and time: two sends on one day are two different emails, and the
   *  time is what tells them apart. In the reader's zone. */
  const whenOf = (entry: StatusSendEntry) =>
    `${format.instantDate(entry.sentAt)}, ${format.time(entry.sentAt)}`

  const languageOf = (language: string) =>
    language === 'ru'
      ? t('common.language.russian')
      : language === 'en'
        ? t('common.language.english')
        : language

  const opened = sends.data?.find((entry) => entry.id === openId)

  const body = sends.isError ? (
    <div className="px-[18px] pb-4">
      <ErrorState error={sends.error} onRetry={() => void sends.refetch()} />
    </div>
  ) : sends.isPending ? (
    <div className="px-[18px] pb-4" aria-busy="true" aria-label={t('cases.statusSends.loading')}>
      <Skeleton className="h-16 rounded-[var(--radius-DEFAULT)]" />
    </div>
  ) : sends.data.length === 0 ? (
    /* One muted line, not an empty-state block: most cases have never had a
       status sent, and a large illustration over the history feed would be
       the loudest thing in the sheet for the least news. */
    <p className="text-text-muted px-[18px] pb-4 text-[13px] leading-snug font-light">
      {t('cases.statusSends.empty')}
    </p>
  ) : (
    /* A list of cards at every width rather than a table: the sheet is a
       narrow column on a desktop and the whole screen on a phone, and five
       facts a row read better stacked than squeezed into columns. */
    <ul aria-labelledby={titleId} className="flex flex-col">
      {sends.data.map((entry) => {
        const status = statusFromWire(entry.caseStatus)
        return (
          <li
            key={entry.id}
            className="border-line-hair flex items-start gap-3 border-b px-[18px] py-3 last:border-b-0"
          >
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="asa-num text-text-heading text-[13px]">
                  {whenOf(entry)}
                </span>
                {/* The case's status AT THE SEND, as a pill. A status this
                    build cannot name prints as the server stored it, in a
                    neutral tag — a pill must not guess a state that is not
                    what the client was told. */}
                {status !== undefined ? (
                  <CaseStatusPill status={status} />
                ) : (
                  <Tag>{entry.caseStatus}</Tag>
                )}
                {entry.batch ? (
                  <span title={t('cases.statusSends.batchHint')}>
                    <Tag>{t('cases.statusSends.batch')}</Tag>
                  </span>
                ) : null}
              </span>
              <span className="text-text-body text-[12.5px] leading-snug font-light">
                {t('cases.statusSends.recipient')}:{' '}
                <span className="text-text-heading font-normal">
                  {entry.recipientLabel || t('cases.statusSends.noRecipientName')}
                </span>
                {entry.recipientEmail ? (
                  <span className="text-text-muted"> &lt;{entry.recipientEmail}&gt;</span>
                ) : null}
              </span>
              <span className="text-text-muted text-[12px] leading-snug font-light">
                {t('cases.statusSends.sentBy')}: {entry.sentByName} · {languageOf(entry.language)}
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={Eye}
              aria-label={t('cases.statusSends.openLabel', { date: whenOf(entry) })}
              onClick={() => setOpenId(entry.id)}
            >
              {t('common.actions.open')}
            </Button>
          </li>
        )
      })}
    </ul>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle id={titleId}>{t('cases.statusSends.title')}</CardTitle>
        <CardSubtitle>{t('cases.statusSends.subtitle')}</CardSubtitle>
      </CardHeader>
      <div className="pt-2">{body}</div>

      {/* Mounted only while an entry is open, so the stored email is read on
          the press and not for every row of the list. */}
      {opened === undefined ? null : (
        <StatusSendViewer
          caseId={caseId}
          entry={opened}
          title={t('cases.statusSends.viewerTitle', { date: whenOf(opened) })}
          onClose={() => setOpenId(null)}
        />
      )}
    </Card>
  )
}

/** One sent email, read-only: its subject and the stored body in the same
 *  sandboxed frame the send sheet previews it in — or, for a summary withheld
 *  from this reader, one muted line and neither. */
function StatusSendViewer({
  caseId,
  entry,
  title,
  onClose,
}: {
  caseId: string
  entry: StatusSendEntry
  title: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const detail = useCaseStatusSend(caseId, entry.id)

  return (
    <Sheet open onClose={onClose} title={title} size="wide">
      {detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : detail.isPending ? (
        <div aria-busy="true" aria-label={t('cases.statusSends.loading')}>
          <Skeleton className="h-[60vh] rounded-[var(--radius-DEFAULT)]" />
        </div>
      ) : detail.data.bodyWithheld === true ? (
        /* The server sent no subject and no HTML for this entry. One muted
           line, not an error: nothing failed, the reader simply may not see
           the cases the summary also covered. */
        <p className="text-text-muted text-[13px] leading-snug font-light">
          {t('cases.statusSends.bodyWithheld')}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-[13px] leading-snug">
            <span className="text-text-muted font-light">{t('cases.statusSends.subject')}:</span>{' '}
            <span className="text-text-heading">{detail.data.subject}</span>
          </p>
          <EmailFrame
            html={detail.data.html}
            title={t('cases.statusSend.preview.frameTitle')}
            height="60vh"
          />
        </div>
      )}
    </Sheet>
  )
}
